import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import WhatsAppButton from "./components/WhatsAppButton"; // legacy (still imported for safety, can be removed)
import ChatWidget from "./components/ChatWidget";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLayout from "./components/AdminLayout";

// Public Pages
import Home from "./pages/Home";
import Subscriptions from "./pages/Subscriptions";
import PartnersPage from "./pages/PartnersPage";
import FAQPage from "./pages/FAQPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import PriceCalculatorV4 from "./pages/PriceCalculatorV4";
import VehicleInfoPage from "./pages/VehicleInfoPage";
import CustomerInfoPage from "./pages/CustomerInfoPage";
import SummaryPage from "./pages/SummaryPage";
import SuccessPage from "./pages/SuccessPage";
import SubscriptionCancelPage from "./pages/SubscriptionCancelPage";
import CGVPage from "./pages/CGVPage";
import MentionsLegalesPage from "./pages/MentionsLegalesPage";
import ProtectionDonneesPage from "./pages/ProtectionDonneesPage";
import GarageMeyrinPage from "./pages/GarageMeyrinPage";
import GarageMeyrinV2Page from "./pages/GarageMeyrinV2Page";
import ServicePage from "./pages/ServicePage";
import { SERVICE_CONFIGS } from "./data/serviceConfigs";
import CookieConsent from "./components/CookieConsent";
import MonAbonnementPage from "./pages/MonAbonnementPage";
import SubscriptionPortalPage from "./pages/SubscriptionPortalPage";
import PaymentMethodPage from "./pages/PaymentMethodPage";
import InvoiceSuccessPage from "./pages/InvoiceSuccessPage";

// Admin Pages
import AdminLoginPage from "./pages/admin/AdminLoginPage";
import ForgotPasswordPage from "./pages/admin/ForgotPasswordPage";
import ResetPasswordPage from "./pages/admin/ResetPasswordPage";
import DashboardPage from "./pages/admin/DashboardPage";
import ProfilePage from "./pages/admin/ProfilePage";
import CustomersListPage from "./pages/admin/CustomersListPage";
import CustomerDetailPage from "./pages/admin/CustomerDetailPage";
import PendingSubscriptionsPage from "./pages/admin/PendingSubscriptionsPage";
import ReviewsManagementPage from "./pages/admin/ReviewsManagementPage";
import PartnersManagementPage from "./pages/admin/PartnersManagementPage";
import AdminUsersManagementPage from "./pages/admin/AdminUsersManagementPage";
import InvoicePaymentsPage from "./pages/admin/InvoicePaymentsPage";
import OcrLogsPage from "./pages/admin/OcrLogsPage";
import SellersPage from "./pages/admin/SellersPage";
import DemandesRdvPage from "./pages/admin/DemandesRdvPage";
import ApiKeysPage from "./pages/admin/ApiKeysPage";
import StripeConfigPage from "./pages/admin/StripeConfigPage";
import SystemPage from "./pages/admin/SystemPage";
import UnifiedLoginPage from "./pages/UnifiedLoginPage";
import NotFoundPage from "./pages/NotFoundPage";
import TrailingSlashRedirect from "./components/TrailingSlashRedirect";

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
          <ScrollToTop />
          <TrailingSlashRedirect>
          <Routes>
            {/* ─── Legacy WordPress URL redirects (SEO) ─── */}
            <Route path="/conditions-generales-dabonnement" element={<Navigate to="/cgv" replace />} />
            <Route path="/conditions-generales-d-abonnement" element={<Navigate to="/cgv" replace />} />
            {/* Public Routes */}
            <Route path="/" element={<><Header /><Home /><Footer /></>} />
            <Route path="/abonnements" element={<><Header /><Subscriptions /><Footer /></>} />
            <Route path="/calculateur/:subscriptionType" element={<><Header /><PriceCalculatorV4 /><Footer /></>} />
            <Route path="/subscription/vehicle-info" element={<><Header /><VehicleInfoPage /><Footer /></>} />
            <Route path="/subscription/customer-info" element={<><Header /><CustomerInfoPage /><Footer /></>} />
            <Route path="/subscription/summary" element={<><Header /><SummaryPage /><Footer /></>} />
            <Route path="/subscription/payment-method" element={<><Header /><PaymentMethodPage /><Footer /></>} />
            <Route path="/subscription/invoice-success" element={<><Header /><InvoiceSuccessPage /><Footer /></>} />
            <Route path="/subscription/success" element={<><Header /><SuccessPage /><Footer /></>} />
            <Route path="/subscription/cancel" element={<><Header /><SubscriptionCancelPage /><Footer /></>} />
            <Route path="/nos-partenaires" element={<><Header /><PartnersPage /><Footer /></>} />
            <Route path="/garage-meyrin" element={<><Header /><GarageMeyrinV2Page /><Footer /></>} />
            <Route path="/garage-meyrin-legacy" element={<><Header /><GarageMeyrinPage /><Footer /></>} />
            <Route path="/garage-meyrin-v2" element={<Navigate to="/garage-meyrin" replace />} />
            {Object.values(SERVICE_CONFIGS).map((cfg) => (
              <Route key={cfg.slug} path={cfg.route} element={<><Header /><ServicePage config={cfg} /><Footer /></>} />
            ))}
            <Route path="/faq" element={<><Header /><FAQPage /><Footer /></>} />
            <Route path="/a-propos-de-nous" element={<><Header /><AboutPage /><Footer /></>} />
            <Route path="/contact" element={<><Header /><ContactPage /><Footer /></>} />
            <Route path="/cgv" element={<><Header /><CGVPage /><Footer /></>} />
            <Route path="/mentions-legales" element={<><Header /><MentionsLegalesPage /><Footer /></>} />
            <Route path="/protection-donnees" element={<><Header /><ProtectionDonneesPage /><Footer /></>} />
            <Route path="/mon-abonnement" element={<><Header /><MonAbonnementPage /><Footer /></>} />
            <Route path="/mon-abonnement/portail" element={<><Header /><SubscriptionPortalPage /><Footer /></>} />

            {/* RDV — gérés désormais par le CRM externe via iframe sur /garage-meyrin */}
            <Route path="/rendez-vous" element={<Navigate to="/garage-meyrin#prendre-rdv" replace />} />
            <Route path="/rendez-vous/succes" element={<Navigate to="/garage-meyrin" replace />} />
            <Route path="/rendez-vous/annulation" element={<Navigate to="/garage-meyrin" replace />} />
            <Route path="/rendez-vous/annulation/:token" element={<Navigate to="/garage-meyrin" replace />} />

            {/* Admin Auth Routes (No Layout) */}
            <Route path="/login" element={<UnifiedLoginPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/admin/reset-password" element={<ResetPasswordPage />} />

            {/* Protected Admin Routes (With Layout) */}
            <Route
              path="/admin/*"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="customers" element={<CustomersListPage />} />
              <Route path="customers/:customerId" element={<CustomerDetailPage />} />
              <Route path="pending-subscriptions" element={<PendingSubscriptionsPage />} />
              <Route path="reviews" element={<ReviewsManagementPage />} />
              <Route path="partners" element={<PartnersManagementPage />} />
              <Route path="invoice-payments" element={<InvoicePaymentsPage />} />
              <Route path="ocr-logs" element={<OcrLogsPage />} />
              <Route path="admin-users" element={<AdminUsersManagementPage />} />
              <Route path="sellers" element={<SellersPage />} />
              <Route path="demandes-rdv" element={<DemandesRdvPage />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
              <Route path="stripe-config" element={<StripeConfigPage />} />
              <Route path="system" element={<SystemPage />} />
            </Route>

            {/* ─── Catch-all 404 (must be last) ─── */}
            <Route path="*" element={<><Header /><NotFoundPage /><Footer /></>} />
          </Routes>
          </TrailingSlashRedirect>
          <Toaster />
          
          {/* Floating AI chat widget — replaces the legacy WhatsApp button. Hidden on /admin. */}
          <ChatWidget />

          {/* Cookie consent banner — gates Meta Pixel + gtag until user accepts */}
          <CookieConsent />
        </BrowserRouter>
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
